"use client"

import { href, Link, useLocation } from "react-router-dom"
import {
  LayoutDashboard,
  User,
  UserSquare2,
  Briefcase,
  FileText,
  Settings,
  IndianRupee,
  Coins,
  Ticket,
  Workflow,
  FormInput,
  Video
} from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import axios from "axios"
import { useSelector } from 'react-redux';
import md5 from "blueimp-md5"
import { useState } from "react";

export default function ModernSidebar({ isOpen, toggleSidebar, isMobile }) {
  const location = useLocation()

  const startupId = localStorage.getItem("startupId")
  const logoUrl = useSelector((state) => state.logo.logoUrl);

  const { data: startupData } = useQuery({
    queryKey: ["startup-profile", startupId],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/startups/${startupId}`)
      return response.data
    },
    enabled: !!startupId,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false, 
    keepPreviousData: true,
  })

  const fullName = startupData?.startupName || ""
  const email = startupData?.email || ""
  const username = startupData?.username || fullName || ""
  const displayInitials = (username || "").slice(0, 2).toUpperCase()

  const gravatarHash = email ? md5(email.trim().toLowerCase()) : null
  const gravatarUrl = gravatarHash
    ? `https://www.gravatar.com/avatar/${gravatarHash}?s=40&d=identicon`
    : null

  const [imageError, setImageError] = useState(false);

  const navItems = [
    { name: "Dashboard", icon: LayoutDashboard, href: "/startup-dashboard" },
    { name: "Profile", icon: User, href: "/profile" },
    { name: "Mentor", icon: UserSquare2, href: "/mentor" },
    { name: "Funding", icon: IndianRupee, href: "/funding" },
    { name: "Forum", icon: FormInput, href: "/forum" },
    { name: "Meetings", icon: Video, href: "/startupmeetings" },
    { name: "CA Portal", icon: FileText, href: "/ca-portal" },
    { name: "Report", icon: Ticket, href: "/reports" },
    { name: "Payment Gateway", icon: Coins, href: "/payment-gateways" },
    { name: "Settings", icon: Settings, href: "/admin-setting" },
  ]

  const getSidebarWidth = () => {
    if (isMobile) return 0
    return 80
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-all duration-300"
          onClick={toggleSidebar}
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 bg-slate-700 text-white transition-all duration-300 border-r border-slate-700/50 shadow-2xl flex flex-col ${isMobile
          ? isOpen
            ? "w-64 xs:w-72 sm:w-80 translate-x-0"
            : "w-64 xs:w-72 sm:w-80 -translate-x-full"
          : "w-16 sm:w-18 md:w-20 lg:w-20 xl:w-20"
          }`}
      >
        {/* Close button for mobile */}
        {isMobile && (
          <button
            onClick={toggleSidebar}
            className="absolute top-3 right-3 xs:top-4 xs:right-4 text-white/70 hover:text-white z-10 p-1.5 xs:p-2 rounded-lg hover:bg-white/10 transition-all duration-200"
          >
            <svg className="w-4 h-4 xs:w-5 xs:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Logo Section - Fixed at top */}
        <div
          className={`flex items-center flex-shrink-0 p-3 xs:p-4 sm:p-6 h-16 xs:h-18 sm:h-20 border-b border-slate-700/50 ${isMobile ? "justify-start" : "justify-center"
            }`}
        >
          <div className="w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 rounded-lg xs:rounded-xl flex items-center justify-center shadow-lg relative group">
            <img src="/tac_logo.png" alt="Tactos Logo" className="w-full h-full object-cover" />

            {/* Logo Tooltip - Only on desktop */}
            {!isMobile && (
              <div
                className="fixed px-3 py-2 bg-slate-800 text-white text-sm rounded-lg shadow-2xl border border-slate-600/50 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-[9999]"
                style={{
                  left: `${getSidebarWidth() + 8}px`,
                  top: "2rem",
                  transform: "translateY(0)",
                }}
              >
                <div className="font-semibold text-white">Tactos Admin</div>
                <div className="text-xs text-slate-300 mt-1">Management Panel</div>

                {/* Arrow */}
                <div
                  className="absolute top-1/2 transform -translate-y-1/2 w-2 h-2 bg-slate-800 rotate-45 border-r border-t border-slate-600/50"
                  style={{ left: "-4px" }}
                ></div>
              </div>
            )}
          </div>

          {/* Mobile logo text */}
          {isMobile && (
            <div className="ml-3 xs:ml-4">
              <span className="text-lg xs:text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Tactos
              </span>
              <p className="text-xs text-slate-400 mt-0.5">Admin Panel</p>
            </div>
          )}
        </div>

        {/* Navigation - Scrollable middle section */}
        <div className="flex-1 overflow-hidden">
          <nav className="h-full overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800 px-2 xs:px-3 py-4 xs:py-6 sm:py-8">
            <ul className="space-y-2 xs:space-y-3">
              {navItems.map((item, index) => {
                const IconComponent = item.icon
                const isActive = location.pathname === item.href

                return (
                  <li key={item.name} className="relative group">
                    <Link
                      to={item.href}
                      className={`flex items-center rounded-lg xs:rounded-xl transition-all duration-300 relative overflow-visible ${isActive
                        ? "bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-400 shadow-lg shadow-blue-500/10 scale-105"
                        : "hover:bg-white/5 text-slate-300 hover:text-white hover:scale-105"
                        } ${isMobile ? "justify-start p-3 xs:p-3.5 sm:p-4" : "justify-center p-2.5 xs:p-3 sm:p-4"}`}
                      onClick={isMobile ? toggleSidebar : undefined}
                    >
                      {/* Active indicator */}
                      {isActive && (
                        <div className="absolute left-0 top-0 bottom-0 w-0.5 xs:w-1 bg-gradient-to-b from-blue-400 to-purple-400 rounded-r-full" />
                      )}

                      {/* Icon */}
                      <div
                        className={`flex-shrink-0 w-5 h-5 xs:w-6 xs:h-6 flex items-center justify-center ${isActive ? "text-blue-400" : ""}`}
                      >
                        <IconComponent className="w-4 h-4 xs:w-5 xs:h-5 sm:w-6 sm:h-6" />
                      </div>

                      {/* Mobile text */}
                      {isMobile && <span className="ml-3 xs:ml-4 font-medium text-sm xs:text-base">{item.name}</span>}

                      {/* Hover effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 to-purple-500/0 hover:from-blue-500/5 hover:to-purple-500/5 rounded-lg xs:rounded-xl transition-all duration-300" />
                    </Link>

                    {/* FIXED Tooltip - Position exactly next to each menu item */}
                    {!isMobile && (
                      <div
                        className="fixed px-3 py-2 bg-slate-800 text-white text-sm rounded-lg shadow-2xl border border-slate-600/50 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-[9999]"
                        style={{
                          left: `${getSidebarWidth() + 8}px`,
                          top: `${120 + (index * 56) + 32}px`, // Logo height + navigation padding + (item height * index) + item center
                          transform: "translateY(-50%)",
                        }}
                      >
                        <div className="font-semibold text-white">{item.name}</div>
                        <div className="text-xs text-slate-300 mt-1">
                          {item.name === "Dashboard" && "Overview & Analytics"}
                          {item.name === "Profile" && "User Information"}
                          {item.name === "Job Portal" && "Job Management"}
                          {item.name === "Funding" && "Financial Resources"}
                          {item.name === "CA Portal" && "Chartered Accountant"}
                          {item.name === "Payment Gateway" && "Payment Resources"}
                          {item.name === "Settings" && "System Configuration"}
                        </div>

                        {/* Arrow pointing to the menu item */}
                        <div
                          className="absolute top-1/2 transform -translate-y-1/2 w-2 h-2 bg-slate-800 rotate-45 border-r border-t border-slate-600/50"
                          style={{ left: "-4px" }}
                        ></div>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </nav>
        </div>

        {/* Bottom User Section - Fixed at bottom for mobile */}
        {isMobile && (
          <div className="flex-shrink-0 p-3 xs:p-4 border-t border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-lg flex items-center justify-center">
                {(!imageError && (logoUrl || gravatarUrl)) ? (
                  <img src={logoUrl || gravatarUrl} alt="logo" className="w-full h-full object-cover rounded-lg" onError={() => setImageError(true)} />
                ) : (
                  <span className="text-xs xs:text-sm font-bold text-white">
                    {displayInitials}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm xs:text-base font-medium text-white truncate">{fullName}</p>
                <p className="text-xs text-slate-400 truncate">{email}</p>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Tooltip Avatar */}
        {!isMobile && (
          <div className="flex-shrink-0 p-4 xs:p-5 sm:p-6 flex justify-center">
            <div className="relative group">
              <div className="w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-green-400 to-blue-500 rounded-lg flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-all duration-300 overflow-hidden">
                {(!imageError && (logoUrl || gravatarUrl)) ? (
                  <img src={logoUrl || gravatarUrl} alt="logo" className="w-full h-full object-cover rounded-lg" onError={() => setImageError(true)} />
                ) : (
                  <span className="text-xs xs:text-sm font-bold text-white">
                    {displayInitials}
                  </span>
                )}
              </div>
              <div
                className="fixed px-3 py-2 bg-slate-800 text-white text-sm rounded-lg shadow-2xl border border-slate-600/50 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-[9999]"
                style={{
                  left: `${getSidebarWidth() + 8}px`,
                  bottom: "2rem",
                }}
              >
                <div className="font-semibold text-white">{fullName}</div>
                <div className="text-xs text-slate-300 mt-1">{email}</div>
                <div className="text-xs text-green-400 mt-1">● Online</div>
                <div
                  className="absolute top-1/2 transform -translate-y-1/2 w-2 h-2 bg-slate-800 rotate-45 border-r border-t border-slate-600/50"
                  style={{ left: "-4px" }}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Enhanced Custom scrollbar styles */}
      <style jsx global>{`
        .scrollbar-thin {
          scrollbar-width: thin;
        }
        
        .scrollbar-thumb-slate-600::-webkit-scrollbar-thumb {
          background-color: rgb(71 85 105);
          border-radius: 0.375rem;
        }
        
        .scrollbar-track-slate-800::-webkit-scrollbar-track {
          background-color: rgb(30 41 59);
        }
        
        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background-color: rgb(71 85 105);
          border-radius: 0.375rem;
        }
        
        .scrollbar-thin::-webkit-scrollbar-track {
          background-color: rgb(30 41 59);
        }
        
        /* Hide scrollbar for mobile but keep functionality */
        @media (max-width: 768px) {
          .scrollbar-thin::-webkit-scrollbar {
            width: 0px;
            background: transparent;
          }
          
          .scrollbar-thin {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
        }
        
        /* Smooth scrolling */
        .scrollbar-thin {
          scroll-behavior: smooth;
        }
        
        /* Touch scrolling for mobile */
        .scrollbar-thin {
          -webkit-overflow-scrolling: touch;
        }

        /* Prevent horizontal overflow */
        body {
          overflow-x: hidden;
        }
      `}</style>
    </>
  )
}
